import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type { FileNode } from "../api.ts";
import { formatCount } from "../lib/format.ts";
import {
  keys,
  messageOf,
  queryClient,
  TRASH_RETENTION_DAYS,
  useDeleteNode,
} from "../lib/queries.ts";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { Icon } from "./Icon.tsx";
import ui from "./ui.module.css";

export function DeleteNodeButton({ node, className }: { node: FileNode; className?: string }) {
  const [open, setOpen] = useState(false);
  const remove = useDeleteNode(node.id);
  const navigate = useNavigate();
  const folder = node.kind === "folder";

  const reach = node.public
    ? "The public link stops working for everyone who has it."
    : node.viewerCount > 0
      ? `The link stops working for the ${formatCount(node.viewerCount, "person", "people")} with access.`
      : "The link stops working.";

  return (
    <>
      <button
        type="button"
        className={`${ui.button} ${className ?? ""}`}
        onClick={() => setOpen(true)}
      >
        <Icon name="trash" />
        Delete
      </button>
      <ConfirmDialog
        open={open}
        title={`Delete ${node.name}?`}
        confirmLabel={remove.isPending ? "Deleting…" : folder ? "Delete folder" : "Delete file"}
        pending={remove.isPending}
        error={remove.error ? messageOf(remove.error) : null}
        onCancel={() => {
          setOpen(false);
          remove.reset();
        }}
        onConfirm={() =>
          remove.mutate(undefined, {
            onSuccess: async () => {
              setOpen(false);
              await (node.parentId
                ? navigate({ to: "/folders/$id", params: { id: node.parentId } })
                : navigate({ to: "/" }));
              queryClient.removeQueries({ queryKey: keys.node(node.id) });
            },
          })
        }
      >
        <p>
          {folder
            ? node.childCount > 0
              ? `The ${formatCount(node.childCount, "item", "items")} inside ${node.childCount === 1 ? "goes" : "go"} with it. ${reach}`
              : reach
            : `${reach}${node.versionCount > 1 ? ` All ${node.versionCount} versions go with it.` : ""}`}
        </p>
        <p>It moves to Trash, where you can put it back for {TRASH_RETENTION_DAYS} days.</p>
      </ConfirmDialog>
    </>
  );
}
